// standards-ignore: co-located-test — shared constants/types + two pure test-id
// helpers with no branching worth testing; exercised through the card tests.
import type { ReviewQuestion, ReviewQuestionConfiguration } from '../logic/reviewTypes';
import type { QuestionConfigFieldErrors } from '../../../shared/domain/questionConfigValidation';

export const CONFIG_BUNDLE = 'features.audit-whiz.translations.reviewQuestions';

/** All-false config field errors — module-level so an omitted `fieldErrors`
 *  prop is reference-stable and a clean card shows no inline errors. */
export const NO_CONFIG_FIELD_ERRORS: QuestionConfigFieldErrors = {
  overview: false,
  division: false,
  auditScore: false,
  tags: false,
};

export const AUDIT_SCORE_MIN = 1;
export const AUDIT_SCORE_MAX = 20;

/**
 * Max width of the question-config field column. The fields occupy roughly the
 * left two-thirds of the card (matching the manual question workflow, which
 * leaves the right third empty) rather than stretching full-bleed. A
 * percentage keeps it responsive across card widths.
 */
export const FIELDS_MAX_WIDTH = '66%';

/** Tracking-id root for the four requirement toggles. */
export const TOGGLE_TRACKING_ROOT = 'audit-whiz.review.q-config';

/** Typed key union for the reviewQuestions bundle's `config*` strings. */
export type ConfigKey =
  | 'configStepHeading'
  | 'configInfoAltText'
  | 'configBasicHeading'
  | 'configDocumentationHeading'
  | 'configOverviewLabel'
  | 'configOverviewInfo'
  | 'configOverviewPlaceholder'
  | 'configBusinessUnitLabel'
  | 'configBusinessUnitInfo'
  | 'configDivisionLabel'
  | 'configDivisionInfo'
  | 'configDivisionPlaceholder'
  | 'configCategoryLabel'
  | 'configCategoryInfo'
  | 'configCategoryPlaceholder'
  | 'configAuditScoreLabel'
  | 'configAuditScoreInfo'
  | 'configAuditScoreValueText'
  | 'configAuditScoreSelectAriaLabel'
  | 'configTagsIncludedLabel'
  | 'configTagsIncludedInfo'
  | 'configTagsIncludedPlaceholder'
  | 'configTagsIncludedRemoveAltText'
  | 'configLocationsExcludedLabel'
  | 'configLocationsExcludedInfo'
  | 'configLocationsExcludedEmpty'
  | 'configLocationsExcludedPlaceholder'
  | 'configLocationRemoveAltText'
  | 'configSourcePolicyLabel'
  | 'configSourcePolicyInfo'
  | 'configSourcePolicyEmpty'
  | 'configCommentOnFailLabel'
  | 'configCommentOnFailInfo'
  | 'configEvidenceOnFailLabel'
  | 'configEvidenceOnFailInfo'
  | 'configCommentOnPassLabel'
  | 'configCommentOnPassInfo'
  | 'configEvidenceOnPassLabel'
  | 'configEvidenceOnPassInfo'
  | 'configDocSopHeading'
  | 'configDocumentationInfo'
  | 'configDocTitlePlaceholder'
  | 'configDocTitleAriaLabel'
  | 'configDocUrlPlaceholder'
  | 'configDocUrlAriaLabel'
  | 'configDocAddButton'
  | 'configDocDocumentColumn'
  | 'configDocLinkColumn'
  | 'configDocRemoveAltText'
  | 'configDocEmptyText'
  | 'configDocInvalidUrl'
  | 'configDocDuplicateUrl'
  | 'configOverviewRequiredError'
  | 'configDivisionRequiredError'
  | 'configAuditScoreRequiredError'
  | 'configTagsRequiredError';

/** The four boolean requirement fields on the configuration. */
export type RequirementToggleField =
  | 'requireCommentOnFail'
  | 'requireEvidenceOnFail'
  | 'requireCommentOnPass'
  | 'requireEvidenceOnPass';

/** Config-driven definition of the four requirement toggles (Fail then Pass),
 *  so they render from one `.map` instead of four near-identical blocks. */
export const REQUIREMENT_TOGGLES: readonly {
  readonly field: RequirementToggleField;
  readonly labelKey: ConfigKey;
  readonly infoKey: ConfigKey;
  readonly trackingSuffix: string;
  readonly testSuffix: string;
}[] = [
  {
    field: 'requireCommentOnFail',
    labelKey: 'configCommentOnFailLabel',
    infoKey: 'configCommentOnFailInfo',
    trackingSuffix: 'require-comment-on-fail',
    testSuffix: 'comment-on-fail',
  },
  {
    field: 'requireEvidenceOnFail',
    labelKey: 'configEvidenceOnFailLabel',
    infoKey: 'configEvidenceOnFailInfo',
    trackingSuffix: 'require-evidence-on-fail',
    testSuffix: 'evidence-on-fail',
  },
  {
    field: 'requireCommentOnPass',
    labelKey: 'configCommentOnPassLabel',
    infoKey: 'configCommentOnPassInfo',
    trackingSuffix: 'require-comment-on-pass',
    testSuffix: 'comment-on-pass',
  },
  {
    field: 'requireEvidenceOnPass',
    labelKey: 'configEvidenceOnPassLabel',
    infoKey: 'configEvidenceOnPassInfo',
    trackingSuffix: 'require-evidence-on-pass',
    testSuffix: 'evidence-on-pass',
  },
];

/**
 * The requirement toggles arranged as a 2×2 grid to match the manual question
 * flow: row 1 = Comment / Attachment on Fail, row 2 = Comment / Attachment on
 * Pass. Derived from {@link REQUIREMENT_TOGGLES} (which is ordered
 * Fail-comment, Fail-attachment, Pass-comment, Pass-attachment) so the two
 * stay in sync.
 */
type RequirementToggle = (typeof REQUIREMENT_TOGGLES)[number];

export const TOGGLE_ROWS: readonly (readonly RequirementToggle[])[] = [
  REQUIREMENT_TOGGLES.slice(0, 2),
  REQUIREMENT_TOGGLES.slice(2, 4),
];

/** Props shared by the Basic Information and Documentation cards: the draft,
 *  a config-patch callback, disabled state, and an optional test-id root. */
export interface QuestionConfigCardProps {
  readonly config: ReviewQuestionConfiguration;
  /** Patches one or more configuration fields and emits the new draft. */
  readonly patchConfig: (patch: Partial<ReviewQuestionConfiguration>) => void;
  /** Per-field inline-error flags (from `questionConfigFieldErrors`). Each
   *  flagged field shows its native error state; defaults to all-false. */
  readonly fieldErrors?: QuestionConfigFieldErrors;
  readonly disabled: boolean;
  readonly dataTestId?: string;
}

/**
 * Spread-friendly `dataTestId` prop builder for our own primitives. Returns
 * the prop object (not a `string | undefined`) so the value stays narrowed to
 * `string` inside it — a plain `{...(id ? { dataTestId: id } : {})}` widens
 * back to `string | undefined`, which `exactOptionalPropertyTypes` rejects.
 */
export function dtProps(
  root: string | undefined,
  suffix: string,
): { dataTestId: string } | Record<string, never> {
  return root ? { dataTestId: `${root}-${suffix}` } : {};
}

/** The native test-id attribute name Stencil's Input/Card/Text forward. */
const NATIVE_TEST_ID_ATTR = 'data-testid';

/** Same as {@link dtProps} for Stencil primitives that take the native
 *  `data-testid` attribute (Input, Card, Text). */
export function nativeDtProps(
  root: string | undefined,
  suffix: string,
): Record<string, string> {
  return root ? { [NATIVE_TEST_ID_ATTR]: `${root}-${suffix}` } : {};
}

/** Builds a patch-config callback bound to a question + change emitter. */
export function buildPatchConfig(
  question: ReviewQuestion,
  onQuestionChange: (next: ReviewQuestion) => void,
): (patch: Partial<ReviewQuestionConfiguration>) => void {
  return (patch) =>
    onQuestionChange({
      ...question,
      configuration: { ...question.configuration, ...patch },
    });
}
